import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import toast from "react-hot-toast";
import Loader from "@mui/material/CircularProgress";
import logo from "../components/assets/im_logo.png";
import Header from "../components/Header/Header";
import Navbar from "../components/Navbar/Navbar";
import { FaRegEyeSlash } from "react-icons/fa";
import { FaRegEye } from "react-icons/fa";

const RegistrationPage = () => {
  const [isPasswordVisible, setPasswordVisible] = useState(false)
  const [isConfirmPasswordVisible, setConfirmPasswordVisible] = useState(false) 
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const API_URL = process.env.REACT_APP_API_URL

  // Mutation to handle registration
  const { mutate, isPending } = useMutation({
    mutationFn: async ({ firstName, middleName, lastName, email, password, confirmPassword }) => {
      const res = await fetch(`${API_URL}/auth/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ firstName, middleName, lastName, email, password, confirmPassword }),
      });

      const data = await res.json();
      
      if (!res.ok) throw new Error(data.error || 'Something went wrong');
      return data;
    },
    onSuccess: (data) => {
      navigate("/verify-otp")
      toast.success(data.message)
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  // Form submission handler
  const handleSubmit = (e) => {
    e.preventDefault();

    const { firstName, middleName, lastName, email, password, confirmPassword } = formData;
    sessionStorage.setItem("email",email)
    mutate({ firstName, middleName, lastName, email, password, confirmPassword });
  };

  // Input change handler
  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (<>
    <Header />
    <Navbar />
    <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-4 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-lg">
        <img alt="Your Company" src={logo} className="mx-auto h-24 w-auto" />
        <h2 className="mt-10 text-center text-2xl/9 font-medium tracking-tight text-gray-900">
          Create a new account
        </h2>
      </div>

      <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-lg">
        <form className="space-y-6" onSubmit={handleSubmit}>
          {/* Name Fields */}
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-900">
              Name
            </label>
            <div className="mt-2 flex space-x-3">
              <input
                id="firstName"
                name="firstName"
                type="text"
                placeholder="First Name"
                required
                className="w-1/3 rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 outline-gray-300 focus:outline focus:outline-2 focus:outline-indigo-600"
                onChange={handleInputChange}
              />
              <input
                id="middleName"
                name="middleName"
                type="text"
                placeholder="Middle Name"
                className="w-1/3 rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 outline-gray-300 focus:outline focus:outline-2 focus:outline-indigo-600"
                onChange={handleInputChange}
              />
              <input
                id="lastName"
                name="lastName"
                type="text"
                placeholder="Last Name"
                required
                className="w-1/3 rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 outline-gray-300 focus:outline focus:outline-2 focus:outline-indigo-600"
                onChange={handleInputChange}
              />
            </div>
          </div>

          {/* Email Field */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-900">
              Email
            </label>
            <div className="mt-2">
              <input
                id="email"
                name="email"
                type="email"
                required
                className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 outline-gray-300 focus:outline focus:outline-2 focus:outline-indigo-600"
                onChange={handleInputChange}
              />
            </div>
          </div>

          {/* Password Fields */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-900">
              Password
            </label>
            <div className="mt-2 relative">
              <input
                id="password"
                name="password"
                type={isPasswordVisible ? "text" : "password"}
                required
                className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 outline-gray-300 focus:outline focus:outline-2 focus:outline-indigo-600"
                onChange={handleInputChange}
              />
              {isPasswordVisible ? <FaRegEye onClick={()=>{setPasswordVisible(!isPasswordVisible)}} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-800 cursor-pointer" size={20} /> : <FaRegEyeSlash onClick={()=>{setPasswordVisible(!isPasswordVisible)}} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-800 cursor-pointer" size={20} />}
            </div>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-900">
              Confirm Password
            </label>
            <div className="mt-2 relative">
              <input
                id="confirmPassword"
                name="confirmPassword"
                type={isConfirmPasswordVisible ? "text" : "password"}
                required
                className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 outline-gray-300 focus:outline focus:outline-2 focus:outline-indigo-600"
                onChange={handleInputChange}
              />
              {isConfirmPasswordVisible ? <FaRegEye onClick={()=>{setConfirmPasswordVisible(!isConfirmPasswordVisible)}} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-800 cursor-pointer" size={20} /> : <FaRegEyeSlash onClick={()=>{setConfirmPasswordVisible(!isConfirmPasswordVisible)}} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-800 cursor-pointer" size={20} />}
            </div>
          </div>

          {/* Submit Button */}
          <div>
            <button
              type="submit"
              className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm/6 font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
              {isPending ? <Loader sx={() => ({ color: "#ffffff" })} size={24} /> : "Continue"}
            </button>
          </div>
        </form>

        {/* Footer */}
        <p className="mt-10 text-center text-sm text-gray-500">
          Already have an account?{" "}
          <Link to="/login" className="font-semibold text-indigo-600 hover:text-indigo-500">
            Log in
          </Link>
        </p>
      </div>
    </div>
    </>
  );
};

export default RegistrationPage;
